import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const STATUS_CONFIG = {
  active:         { label: 'Available',      color: '#16a34a' },
  down:           { label: 'Down',           color: '#dc2626' },
  in_shop:        { label: 'In Shop',        color: '#d97706' },
  out_of_service: { label: 'Out of Service', color: '#9ca3af' },
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Available' },
  { value: 'down', label: 'Down' },
  { value: 'in_shop', label: 'In Shop' },
  { value: 'out_of_service', label: 'Out of Service' },
];

export default function TruckAvailabilityPage() {
  const { user, isAtLeastAdmin } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canChangeStatus = isAtLeastAdmin || (user?.team_name || '') === 'Truck Maintenance' || user?.role === 'maintenance';

  async function loadTrucks() {
    try {
      const data = await api.getTrucks();
      const list = Array.isArray(data) ? data : (data.trucks || []);
      // Only show active (non-deactivated) trucks
      setTrucks(list.filter(t => t.is_active !== 0));
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load trucks');
    }
    setLoading(false);
  }

  useEffect(() => {
    loadTrucks();
    const interval = setInterval(loadTrucks, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  async function handleStatusChange(truckId, newStatus) {
    try {
      const truck = trucks.find(t => t.id === truckId);
      if (!truck) return;
      await api.updateTruck(truckId, { ...truck, status: newStatus });
      setTrucks(prev => prev.map(t => t.id === truckId ? { ...t, status: newStatus } : t));
    } catch (err) {
      setError(err.message || 'Failed to update truck status');
    }
  }

  function formatMileage(miles) {
    if (!miles && miles !== 0) return '\u2014';
    return Number(miles).toLocaleString() + ' mi';
  }

  return (
    <div className="fleet-status">
      <div className="fleet-status__header">
        <h1>Fleet Status</h1>
        {canChangeStatus && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
            You can change truck status from this page or from Trucks management.
          </p>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-spinner">Loading fleet status...</div>
      ) : trucks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          No trucks in the fleet yet.
        </div>
      ) : (
        <div className="fleet-status__grid">
          {trucks.map(truck => {
            const status = truck.status || 'active';
            const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;

            return (
              <div key={truck.id} className={`fleet-card fleet-card--${status}`}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <span
                    className={`fleet-card__dot fleet-card__dot--${status}`}
                    title={config.label}
                  />
                  <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>
                    Truck #{truck.unit_number}
                  </span>
                </div>

                {(truck.year || truck.make || truck.model) && (
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    {[truck.year, truck.make, truck.model].filter(Boolean).join(' ')}
                  </div>
                )}

                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span>
                    Status:{' '}
                    <strong style={{ color: config.color }}>{config.label}</strong>
                  </span>
                  {truck.region && <span>Region: {truck.region}</span>}
                  <span>Mileage: {formatMileage(truck.current_mileage)}</span>
                  {truck.assigned_driver_name && (
                    <span>Driver: {truck.assigned_driver_name}</span>
                  )}
                </div>

                {canChangeStatus && (
                  <div style={{ marginTop: '14px' }}>
                    <select
                      className="form-input"
                      value={status}
                      onChange={e => handleStatusChange(truck.id, e.target.value)}
                      style={{ fontSize: '0.85rem', padding: '6px 10px' }}
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .fleet-status { padding: 24px; }
        .fleet-status__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 8px;
        }
        .fleet-status__header h1 { margin: 0; }
        .fleet-status__grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }
        .fleet-card {
          background: white;
          border-radius: var(--radius-md, 8px);
          padding: 20px;
          box-shadow: var(--shadow-card, 0 1px 3px rgba(0,0,0,0.1));
          border: 1px solid var(--border-light, #e5e7eb);
          border-left: 4px solid;
          transition: box-shadow 0.15s;
        }
        .fleet-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
        .fleet-card--active { border-left-color: #16a34a; }
        .fleet-card--down { border-left-color: #dc2626; }
        .fleet-card--in_shop { border-left-color: #d97706; }
        .fleet-card--out_of_service { border-left-color: #9ca3af; }

        .fleet-card__dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          display: inline-block;
          margin-right: 8px;
          flex-shrink: 0;
        }
        .fleet-card__dot--active { background: #16a34a; }
        .fleet-card__dot--down { background: #dc2626; animation: fleetPulse 2s infinite; }
        .fleet-card__dot--in_shop { background: #d97706; }
        .fleet-card__dot--out_of_service { background: #9ca3af; }

        @keyframes fleetPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
